import { useClient, useSalesLeadAction, useSalesLeads } from '@/api/hooks';
import { SalesSubnav } from '@/components/sales/SalesSubnav';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { getErrorMessage } from '@/lib/api';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

export function SalesLeadsPage() {
  const { data: client, isLoading: clientLoading } = useClient();
  const { data, isLoading } = useSalesLeads();
  const action = useSalesLeadAction();
  const [actionError, setActionError] = useState<string | null>(null);

  if (!clientLoading && !client?.modules?.animal_sales) {
    return <Navigate to="/" replace />;
  }

  const leads = data?.leads ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads de vendas"
        subtitle={
          data ? `${data.unread_count} não lidos` : 'Interesses capturados na página pública'
        }
      />

      <SalesSubnav />

      {actionError && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {actionError}
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-manejai-bark/60">Carregando…</p>
      ) : leads.length === 0 ? (
        <p className="text-sm text-manejai-bark/60">Nenhum lead pendente.</p>
      ) : (
        <div className="grid gap-3">
          {leads.map((lead) => (
            <div key={lead.id} className="rounded-2xl border border-manejai-sand bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-manejai-forest">{lead.name}</h2>
                    {!lead.read_at && <Badge tone="info">Novo</Badge>}
                    {lead.waitlist && <Badge tone="warning">Lista de espera</Badge>}
                  </div>
                  <p className="text-sm text-manejai-bark/70">
                    {lead.lot?.title || `Lote #${lead.lot_id}`} · quer {lead.shares_wanted} cota(s)
                  </p>
                  <p className="mt-1 text-sm text-manejai-bark/60">
                    {lead.phone || '—'} · {lead.email || '—'}
                  </p>
                  {lead.message && <p className="mt-2 text-sm">{lead.message}</p>}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={action.isPending}
                    title="Cria o comprador e reserva a primeira cota disponível do lote"
                    onClick={async () => {
                      setActionError(null);
                      try {
                        await action.mutateAsync({ id: lead.id, action: 'convert' });
                      } catch (err) {
                        setActionError(
                          getErrorMessage(err, 'Não foi possível converter o lead.'),
                        );
                      }
                    }}
                  >
                    Reservar cota
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      setActionError(null);
                      try {
                        await action.mutateAsync({ id: lead.id, action: 'discard' });
                      } catch (err) {
                        setActionError(getErrorMessage(err, 'Não foi possível descartar.'));
                      }
                    }}
                  >
                    Descartar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
